import React from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Flex, Card, Row, Col, Typography, Button, message, Select, Spin, Input, Checkbox } from 'antd'
import { ChevronDown } from 'lucide-react'
import { CopyOutlined } from '@ant-design/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthContext } from '../context/auth'
import { WidgetFormat } from '../../../types/widget-settings'

import formatDate from '../utils/date-formatter'
import { StyledDatePicker } from '../components/ui/datepicker'
import { LOREM_IPSUM } from '../constants/lorem-ipsum'

import iPhoneFrame from '../images/iphone-frame.png'
import { StickyHTML } from '../utils/sticky-HTML'

const { Paragraph, Title } = Typography

const SCRIPT_CODE = process.env.REACT_APP_SCRIPT_CODE!
const SCRIPT_CODE_STICKY = process.env.REACT_APP_SCRIPT_CODE_STICKY!

const sizeOptions = [
  { key: '1', label: '300x250', width: '300', height: '250' },
  { key: '2', label: '300x600', width: '300', height: '600' },
  { key: '3', label: '100%x600', width: '100%', height: '600' },
  { key: '4', label: 'Adaptativo', width: '100%', height: 'auto' },
]

const modeOptions = [
  { key: 'carousel', label: 'Carousel' },
  { key: 'oustream', label: 'Oustream' },
  { key: 'inRead', label: 'InRead' },
  { key: 'sticky', label: 'Sticky' },
]

interface PositionConfig {
  top: string
  bottom: string
  left: string
  right: string
}

interface SizeOption {
  key: string
  label: string
  width: string
  height: string
}

const DoerCarousel = (props: JSX.IntrinsicElements['doer-carousel']) => {
  return React.createElement('doer-carousel', props)
}

const DrupiSticky = (props: JSX.IntrinsicElements['drupi-sticky']) => {
  return React.createElement('drupi-sticky', props)
}

// Componente para el código incrustable
const EmbedCodeSection: React.FC<{ scriptCode: string; widgetCode: string }> = ({ scriptCode, widgetCode }) => {
  const copyScript = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        message.success('Código copiado al portapapeles')
      })
      .catch(() => {
        message.error('Error al copiar el código')
      })
  }

  return (
    <Card>
      <Title level={3} style={{ marginTop: '0' }}>
        Código para incrustar
      </Title>
      <Paragraph>Copia el siguiente código y pégalo en la sección {'<head>'} de tu página web</Paragraph>

      <Paragraph>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <pre
            style={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '8px 12px',
              borderRadius: '4px',
              overflowX: 'auto',
              flex: 1,
            }}
          >
            <Typography.Text
              code
              style={{ fontSize: '11px', fontFamily: 'monospace' }}
            >{`<script src='${scriptCode}'></script>`}</Typography.Text>
          </pre>
          <Button icon={<CopyOutlined />} onClick={() => copyScript(`<script src='${scriptCode}'></script>`)} />
        </div>
      </Paragraph>

      <Paragraph>
        Copia el siguiente código y pégalo en tu sitio web donde desees que se muestre el componente
      </Paragraph>

      <Paragraph>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <pre
            style={{
              backgroundColor: '#f5f5f5',
              padding: '8px 12px',
              borderRadius: '4px',
              overflowX: 'auto',
              flex: 1,
            }}
          >
            <Typography.Text code style={{ fontSize: '11px', fontFamily: 'monospace' }}>
              {widgetCode}
            </Typography.Text>
          </pre>
          <Button icon={<CopyOutlined />} onClick={() => copyScript(widgetCode)} />
        </div>
      </Paragraph>
    </Card>
  )
}

// Componente para los inputs de posición en modo oustream
const OustreamInputs: React.FC<{
  position: PositionConfig
  setPosition: React.Dispatch<React.SetStateAction<PositionConfig>>
}> = ({ position, setPosition }) => {
  const handleChange = (key: keyof PositionConfig, value: string) => {
    setPosition(prev => ({ ...prev, [key]: value }))
  }

  return (
    <motion.div
      key='oustream-inputs'
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{ overflow: 'hidden' }}
    >
      <Flex gap='small' style={{ width: '400px' }}>
        <Input
          placeholder='Top (px)'
          type='number'
          pattern='[0-9]*'
          value={position.top}
          onChange={e => handleChange('top', e.target.value)}
        />
        <Input
          placeholder='Left (px)'
          type='number'
          pattern='[0-9]*'
          value={position.left}
          onChange={e => handleChange('left', e.target.value)}
        />
        <Input
          placeholder='Bottom (px)'
          type='number'
          pattern='[0-9]*'
          value={position.bottom}
          onChange={e => handleChange('bottom', e.target.value)}
        />
        <Input
          placeholder='Right (px)'
          type='number'
          pattern='[0-9]*'
          value={position.right}
          onChange={e => handleChange('right', e.target.value)}
        />
      </Flex>
    </motion.div>
  )
}

const MobileFrame: React.FC<{ html: string }> = ({ html }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  return (
    <div
      style={{
        position: 'relative',
        width: '900px',
        height: '900px',
      }}
    >
      {/* Fondo del iPhone */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${iPhoneFrame})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Iframe detrás del iPhone */}
      <iframe
        ref={iframeRef}
        srcDoc={html}
        sandbox='allow-scripts allow-same-origin allow-popups'
        title='Widget Preview Frame'
        style={{
          position: 'absolute',
          top: '80px',
          left: '277px',
          width: '350px',
          height: '753px',
          border: 'none',
          borderRadius: '50px',
          borderBottomRightRadius: '70px',
          backgroundColor: 'transparent',
          zIndex: 0,
          pointerEvents: 'auto',
        }}
      />
    </div>
  )
}

export const WidgetPreviewPage: React.FC<{ widgetId: string }> = ({ widgetId }) => {
  const { session } = useAuthContext()

  const [loadingWidget, setLoadingWidget] = React.useState(false)
  const [selectedSize, setSelectedSize] = React.useState<SizeOption>(sizeOptions[0])
  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs())
  const [selectedMode, setSelectedMode] = React.useState<WidgetFormat>('carousel')
  const [widgetError, setWidgetError] = React.useState(false)

  const [showStickyFrame, setShowStickyFrame] = React.useState(true)

  const currentScriptCode = selectedMode === 'sticky' ? SCRIPT_CODE_STICKY : SCRIPT_CODE

  // Estados de posición para el modo oustream
  const [position, setPosition] = React.useState<PositionConfig>({
    top: '',
    bottom: '50',
    left: '',
    right: '50',
  })

  // Clave para forzar la recarga del widget cuando cambian los parámetros
  const combinedKey = `${widgetId}|${formatDate(selectedDate)}|${selectedSize.key}|${selectedMode}|${position.top}|${position.bottom}|${position.left}|${position.right}`

  // Genera el código del widget basado en los parámetros actuales
  const generateWidgetCode = (): string => {
    // Caso especial para sticky
    if (selectedMode === 'sticky') {
      return `<drupi-sticky widgetId='${widgetId}' />`
    }

    // Caso para adaptativo (100%)
    if (selectedSize.key === '4') {
      return `<doer-carousel widgetId='${widgetId}'${selectedMode === 'inRead' ? ` in-read='true'` : ''} />`
    }

    // Caso para ancho 100%
    if (selectedSize.key === '3') {
      return `<doer-carousel widgetId='${widgetId}' height='600'${selectedMode === 'inRead' ? ` in-read='true'` : ''} />`
    }

    // Para todos los demás casos
    const baseProps = `widgetId='${widgetId}' width='${selectedSize.width}' height='${selectedSize.height}'`

    // Agregar propiedades adicionales según el modo
    const additionalProps = []

    if (selectedMode === 'inRead') {
      additionalProps.push(`in-read='true'`)
    } else if (selectedMode === 'oustream') {
      if (position.top) additionalProps.push(`w-top='${position.top}'`)
      if (position.bottom) additionalProps.push(`w-bottom='${position.bottom}'`)
      if (position.left) additionalProps.push(`w-left='${position.left}'`)
      if (position.right) additionalProps.push(`w-right='${position.right}'`)
    }

    return `<doer-carousel ${baseProps}${additionalProps.length ? ' ' + additionalProps.join(' ') : ''} />`
  }

  const widgetCode = generateWidgetCode()

  const getCarouselProps = () => {
    const props: Record<string, any> = {
      widgetId,
      width: selectedSize.width,
      height: selectedSize.height,
      date: formatDate(selectedDate),
      'disable-cache': session?.id_token,
    }

    if (selectedMode === 'inRead') {
      props['in-read'] = 'true'
    }

    if (selectedMode === 'oustream') {
      if (position.top) props['w-top'] = position.top
      if (position.bottom) props['w-bottom'] = position.bottom
      if (position.left) props['w-left'] = position.left
      if (position.right) props['w-right'] = position.right
      props['disable-close'] = true
    }

    return props
  }

  const getStickyProps = () => {
    return {
      widgetId,
      date: formatDate(selectedDate),
      'disable-cache': session?.id_token,
    }
  }

  // Manejo de errores de fetch
  React.useEffect(() => {
    const originalFetch = window.fetch

    const interceptFetch = async (...args: Parameters<typeof fetch>) => {
      const [resource] = args
      const url = typeof resource === 'string' ? resource : resource instanceof Request ? resource.url : ''

      try {
        const response = await originalFetch(...args)

        if (url.includes('/carousel/preview/') && response.status === 400) {
          setWidgetError(true)
        }

        return response
      } catch (err) {
        return Promise.reject(err)
      }
    }

    window.fetch = interceptFetch

    return () => {
      window.fetch = originalFetch
    }
  }, [])

  React.useEffect(() => {
    setWidgetError(false)
  }, [combinedKey])

  // Carga del script para el widget
  React.useEffect(() => {
    const existingScript = document.querySelector(`script[src="${currentScriptCode}"]`)

    if (!existingScript) {
      const scriptElement = document.createElement('script')
      scriptElement.src = currentScriptCode
      scriptElement.async = true
      document.head.appendChild(scriptElement)
    }
  }, [currentScriptCode])

  // Simular carga del widget cuando cambian los parámetros
  React.useEffect(() => {
    setLoadingWidget(true)
    const timeout = setTimeout(() => {
      setLoadingWidget(false)
    }, 1000)

    return () => clearTimeout(timeout)
  }, [combinedKey])

  // Función para renderizar el widget
  const renderWidget = () => {
    if (loadingWidget) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size='small' />
        </div>
      )
    }

    if (widgetError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#999',
          }}
        >
          Este widget se está procesando. Por favor, intentá más tarde.
        </div>
      )
    }

    if (selectedMode === 'sticky' && showStickyFrame) {
      const htmlString = StickyHTML(widgetId, formatDate(selectedDate), session?.id_token!, currentScriptCode)
      return <MobileFrame html={htmlString} />
    }

    const widgetProps = selectedMode === 'sticky' ? getStickyProps() : getCarouselProps()
    const WidgetComponent = selectedMode === 'sticky' ? DrupiSticky : DoerCarousel

    return <WidgetComponent key={combinedKey} {...widgetProps} />
  }

  return (
    <Flex justify='space-around'>
      <Row>
        <Col style={{ width: '1100px', margin: '0 auto' }}>
          {/* Título arriba */}
          <Typography.Title level={2} style={{ marginBottom: '16px' }}>
            Vista previa del widget
          </Typography.Title>

          {/* Controles de configuración */}
          <Flex align='flex-start' gap='small' style={{ width: '1000px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <StyledDatePicker
              widgetId={widgetId}
              value={selectedDate}
              onChange={setSelectedDate}
              allowClear={false}
              style={{ borderColor: 'rgba(138, 148, 166, 1)', borderRadius: '8px', height: '32px' }}
            />
            <Select
              value={selectedSize.key}
              onChange={key => {
                const option = sizeOptions.find(o => o.key === key)
                if (option) setSelectedSize(option)
              }}
              options={sizeOptions.map(option => ({
                label: option.label,
                value: option.key,
              }))}
              suffixIcon={<ChevronDown width={12} />}
              className='custom-select'
              disabled={selectedMode === 'sticky'}
            />
            <Select
              value={selectedMode}
              onChange={setSelectedMode}
              options={modeOptions.map(option => ({
                label: option.label,
                value: option.key,
              }))}
              suffixIcon={<ChevronDown width={12} />}
              className='custom-select'
            />

            {selectedMode === 'sticky' && (
              <Checkbox
                checked={showStickyFrame}
                onChange={e => setShowStickyFrame(e.target.checked)}
                style={{ height: '32px', justifyContent: 'center', alignItems: 'center' }}
              >
                Mobile frame
              </Checkbox>
            )}

            <AnimatePresence>
              {selectedMode === 'oustream' && <OustreamInputs position={position} setPosition={setPosition} />}
            </AnimatePresence>

            <style>
              {`
                .custom-select .ant-select-selector {
                  border: 1px solid rgba(138, 148, 166, 1) !important;
                  border-radius: 8px;
                  height: 32px !important;
                  width: 100px !important;
                }
              `}
            </style>
          </Flex>

          {/* Texto antes del widget */}
          {(selectedMode === 'inRead' || (selectedMode === 'sticky' && !showStickyFrame)) && (
            <Col style={{ width: 600, margin: '0 auto', paddingBottom: 20 }}>
              <Typography.Paragraph style={{ padding: '16px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                {LOREM_IPSUM}
              </Typography.Paragraph>
            </Col>
          )}
          {/* Widget */}
          <Flex
            style={{
              height:
                selectedMode === 'sticky'
                  ? '900px'
                  : selectedSize.height === 'auto'
                    ? '300px'
                    : `${selectedSize.height}px`,
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {renderWidget()}
          </Flex>

          {/* Texto después del widget */}
          {(selectedMode === 'inRead' || (selectedMode === 'sticky' && !showStickyFrame)) && (
            <Col style={{ width: 600, margin: '0 auto', paddingBottom: 20 }}>
              <Typography.Paragraph style={{ padding: '16px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                {LOREM_IPSUM}
              </Typography.Paragraph>
            </Col>
          )}

          {/* Embed code */}
          <Col
            style={{
              marginTop: selectedMode === 'sticky' ? '0px' : '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <EmbedCodeSection scriptCode={currentScriptCode} widgetCode={widgetCode} />
          </Col>
        </Col>
      </Row>
    </Flex>
  )
}
